import { ReactElement, ReactNode, useCallback, useMemo, useState } from 'react';
import styles from './task-events-list.module.scss';
import {
    UIAvatar,
    UIErrorState,
    UIIcon,
    UILoadingState,
    UIPopover,
    UIText,
    UITextButton,
    UITooltip
} from '@/ui';
import { Task, TaskEvent } from '@/api';
import {
    blue,
    concrete,
    fontColorLight,
    mint,
    moonlight,
    opacity,
    red
} from '@/ui/styles';
import { formatDuration, formatGpsCoordinates } from '../util/task-helpers';
import {
    getEmergencyServiceLabel,
    getInjuryLabel,
    getResolveReasonLabel
} from '../util/resolve-vocabulary';
import { useFormatDate } from '@/common/hooks';
import { useEnableTaskEventsSockets, useFetchTaskEvents } from '../queries';
import { useMe } from '@/me/providers';
import { AcknowledgeAlertButton } from './acknowledge-alert-button';
import { AddNoteButton } from './add-note-modal';
import { AssignAlertButton } from './assign-alert-button';
import { ResolveAlertButton } from './resolve-alert-modal';
import { useTaskPermissions, useTasksTranslation } from '../hooks';
import { TaskInfoItem } from './task-info-item';

export type TaskEventsListProps = {
    task: Task;
};
export function TaskEventsList(props: TaskEventsListProps): ReactElement {
    const { task } = props;
    const { t } = useTasksTranslation();
    const { events, status, refetch } = useFetchTaskEvents(task.id);
    useEnableTaskEventsSockets(task.id, task.companyId);

    return (
        <div className={styles.container}>
            {status === 'pending' && <UILoadingState />}
            {status === 'error' && (
                <UIErrorState
                    title={t('components.task_events_list.load_error')}
                    onRetry={refetch}
                />
            )}
            {status === 'success' && (
                <div className={styles.listContainer}>
                    <TaskEventAction task={task} />
                    <div style={{ height: 16 }} />
                    {events
                        .map((event, index) => (
                            <TaskEventItem
                                key={event.id}
                                event={event}
                                isLast={index === 0}
                            />
                        ))
                        .reverse()}
                    <div style={{ height: 16 }} />
                </div>
            )}
        </div>
    );
}

const IMAGE_SIZE = 32;
const SPACE_BETWEEN_ITEMS = 24;

type TaskEventItemProps = {
    event: TaskEvent;
    isLast: boolean;
};
function TaskEventItem(props: TaskEventItemProps): ReactElement {
    const { event, isLast } = props;
    const { t } = useTasksTranslation();
    const { formatSmartShortTimeAndDate } = useFormatDate();

    function getStatusLineColor(): string {
        if (event.taskStatus.endsWith('ALERT')) {
            return red;
        } else if (event.taskStatus === 'COMPLETED') {
            return mint;
        } else {
            return blue;
        }
    }

    // A switch of literal `t()` calls rather than a key built from
    // `event.type`: an interpolated key is one the checker cannot verify, and
    // this is exactly the set a rename would silently break.
    function getEventDescription(): string {
        switch (event.type) {
            case 'CREATE':
                return t('shared.tasks.events.create');
            case 'EXTEND':
                if (!event.additionalMinutes) {
                    return t('shared.tasks.events.extend');
                }
                return t('shared.tasks.events.extend_with_duration', {
                    duration: formatDuration(t, event.additionalMinutes)
                });
            case 'COMPLETE':
                return t('shared.tasks.events.complete');
            case 'EXPIRED_ALERT':
                return t('shared.tasks.events.expired_alert');
            case 'MANUAL_ALERT':
            case 'ACTIVE_MANUAL_ALERT':
            case 'INACTIVE_MANUAL_ALERT':
                return t('shared.tasks.events.manual_alert');
            case 'DURESS_ALERT':
                return t('shared.tasks.events.duress_alert');
            case 'CHECK_IN_ALERT':
                return t('shared.tasks.events.check_in_alert');
            case 'FALL_MOTION_ALERT':
                return t('shared.tasks.events.fall_motion_alert');
            case 'STILL_MOTION_ALERT':
                return t('shared.tasks.events.still_motion_alert');
            case 'GEOFENCE_ALERT':
                return t('shared.tasks.events.geofence_alert');
            case 'TILT_ALERT':
                return t('shared.tasks.events.tilt_alert');
            case 'ALERT_CANCELLED':
                return t('shared.tasks.events.alert_cancelled');
            case 'ALERT_RESOLVED':
                return t('shared.tasks.events.alert_resolved');
            case 'ALERT_ACKNOWLEDGED':
                return t('shared.tasks.events.alert_acknowledged');
            case 'ALERT_ASSIGNED':
                return t('shared.tasks.events.alert_assigned');
            case 'ALERT_UNASSIGNED':
                return t('shared.tasks.events.alert_unassigned');
            case 'ALERT_PASSBACK':
                if (event.passback) {
                    if (
                        (event.passback as unknown as { reason: string })
                            .reason === 'CUSTOMER_REQUESTED'
                    ) {
                        return t(
                            'shared.tasks.events.passback_customer_requested'
                        );
                    } else if (
                        (event.passback as unknown as { reason: string })
                            .reason === 'UNCONTACTABLE'
                    ) {
                        return t('shared.tasks.events.passback_uncontactable');
                    }
                }
                return t('shared.tasks.events.unknown');
            case 'NOTE':
                return t('shared.tasks.events.note');
            default:
                return t('shared.tasks.events.unknown');
        }
    }

    const leftBorderColor = getStatusLineColor();
    const eventDescription = getEventDescription();
    const timeString = useMemo(
        () => formatSmartShortTimeAndDate(new Date(event.createdAt)),
        [event.createdAt, formatSmartShortTimeAndDate]
    );

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                marginLeft: 16,
                marginRight: 16
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 8
                }}
            >
                {/* Status line */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        height: IMAGE_SIZE
                    }}
                >
                    <div
                        style={{
                            width: 4,
                            height: IMAGE_SIZE,
                            borderRadius: 2,
                            backgroundColor: leftBorderColor
                        }}
                    />
                </div>

                {/* Avatar */}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <UIAvatar
                        img={event.displayInfo.image}
                        alt={event.displayInfo.name}
                        subAvatar={
                            event.displayInfo.subImage
                                ? {
                                      img: event.displayInfo.subImage,
                                      alt: event.displayInfo.subText ?? ''
                                  }
                                : undefined
                        }
                        size={IMAGE_SIZE}
                    />
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            width: IMAGE_SIZE,
                            height: '100%'
                        }}
                    >
                        <div
                            style={{
                                width: 2,
                                height: '100%',
                                backgroundColor: concrete,
                                opacity: isLast ? 0 : 1
                            }}
                        />
                    </div>
                </div>

                {/* User name and event type */}
                <div
                    style={{
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                        gap: 8
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2
                        }}
                    >
                        <UIText>{event.displayInfo.name}</UIText>
                        <UIText type="default2">{eventDescription}</UIText>
                    </div>
                </div>

                {/* Time */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: IMAGE_SIZE
                    }}
                >
                    <UIText type="default2" variant="small">
                        {timeString}
                    </UIText>
                </div>
            </div>

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 8
                }}
            >
                {/* Status line  */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: 4
                    }}
                />

                {/* Line */}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            width: IMAGE_SIZE,
                            height: '100%',
                            minHeight: SPACE_BETWEEN_ITEMS
                        }}
                    >
                        <div
                            style={{
                                width: 2,
                                height: '100%',
                                minHeight: SPACE_BETWEEN_ITEMS,
                                backgroundColor: concrete,
                                opacity: isLast ? 0 : 1
                            }}
                        />
                    </div>
                </div>

                {/* Data */}
                <TaskEventDataInfo event={event} />
            </div>
        </div>
    );
}

type TaskEventDataInfoProps = {
    event: TaskEvent;
};
function TaskEventDataInfo(props: TaskEventDataInfoProps): ReactElement {
    const { event } = props;
    const { t } = useTasksTranslation();

    const [seeMore, setSeeMore] = useState(false);

    const items = useCallback(() => {
        const items: ReactNode[] = [];
        if (event.resolved) {
            if (event.resolved.isFalseAlarm) {
                items.push(
                    <div className={styles.eventItemDataItem}>
                        <UIText variant="small" type="default2">
                            {t('components.task_events_list.label_reasons')}
                        </UIText>
                        <UIText>
                            {t('components.task_events_list.false_alarm')}
                        </UIText>
                    </div>
                );
            } else {
                items.push(
                    <div className={styles.eventItemDataItem}>
                        <UIText variant="small" type="default2">
                            {t('components.task_events_list.label_reasons')}
                        </UIText>
                        <UIText>
                            {event.resolved.reasons
                                .map((reason) =>
                                    getResolveReasonLabel(t, reason)
                                )
                                .join(t('shared.tasks.list_separator'))}
                        </UIText>
                    </div>
                );
                if (event.resolved.injury) {
                    items.push(
                        <div className={styles.eventItemDataItem}>
                            <UIText variant="small" type="default2">
                                {t(
                                    'components.task_events_list.label_injuries'
                                )}
                            </UIText>
                            <UIText>
                                {getInjuryLabel(t, event.resolved.injury)}
                            </UIText>
                        </div>
                    );
                }

                items.push(
                    <div className={styles.eventItemDataItem}>
                        <UIText variant="small" type="default2">
                            {t(
                                'components.task_events_list.label_emergency_services'
                            )}
                        </UIText>
                        <UIText>
                            {event.resolved.emergencyServices
                                .map((service) =>
                                    getEmergencyServiceLabel(t, service)
                                )
                                .join(t('shared.tasks.list_separator'))}
                        </UIText>
                    </div>
                );
            }
        }

        if (event.note) {
            items.push(
                <div className={styles.eventItemDataItem}>
                    <UIText variant="small" type="default2">
                        {t('components.task_events_list.label_note')}
                    </UIText>
                    <UIText>{event.note}</UIText>
                </div>
            );
        }

        return items;
    }, [event, t]);

    const hasItems = items().length > 0;

    const fullAddressText = event.location?.fullGeo ?? '';
    const [gpsCoordinateText, gpsCoordinateCopyText] = useMemo(() => {
        if (event?.location) {
            const lat = event.location.lat;
            const lng = event.location.lng;
            const acc = event.location.acc;

            const text = formatGpsCoordinates(t, lat, lng, acc);
            const copyText = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;

            return [text, copyText];
        } else {
            return [t('shared.tasks.no_gps_data'), null];
        }
    }, [event.location, t]);

    return (
        <div
            style={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                paddingBottom: 0,
                marginBottom: SPACE_BETWEEN_ITEMS
            }}
        >
            {event.location && event.location.shortGeo && (
                <div
                    className={`${styles.eventItemDataItem} ${styles.eventItemDataItemSpacer}`}
                >
                    <UIPopover
                        placement="auto"
                        trigger="hover"
                        triggerNode={
                            <div>
                                <UIIcon
                                    name="map-pin"
                                    size={12}
                                    color={fontColorLight}
                                />
                                <UIText type="default2">
                                    {' ' + event.location.shortGeo}
                                </UIText>
                            </div>
                        }
                    >
                        <div className={styles.popoverContentContainer}>
                            <TaskInfoItem
                                title={t('shared.tasks.address')}
                                text={fullAddressText}
                                copyToClipboardText={fullAddressText}
                            />
                            <TaskInfoItem
                                title={t('shared.tasks.gps_coordinates')}
                                text={gpsCoordinateText}
                                copyToClipboardText={gpsCoordinateCopyText}
                            />
                        </div>
                    </UIPopover>
                </div>
            )}
            {event.location && event.location.w3w && (
                <div
                    className={`${styles.eventItemDataItem} ${styles.eventItemDataItemSpacer}`}
                >
                    <UITooltip
                        text={t('components.task_events_list.w3w_tooltip')}
                    >
                        <UIText type="error">
                            {'/// '}
                            <UIText type="link">{event.location.w3w}</UIText>
                        </UIText>
                    </UITooltip>
                </div>
            )}
            {event.beaconSnapshot.length > 0 && event.beaconsUpdatedAt && (
                <div
                    className={`${styles.eventItemDataItem} ${styles.eventItemDataItemSpacer}`}
                >
                    <UIPopover
                        placement="auto"
                        trigger="hover"
                        triggerNode={
                            <div>
                                <UIIcon
                                    name="disc"
                                    size={12}
                                    color={fontColorLight}
                                />
                                <UIText type="default2">
                                    {' '}
                                    {t('shared.tasks.nearby_beacons_count', {
                                        count: event.beaconSnapshot.length
                                    })}
                                </UIText>
                            </div>
                        }
                    >
                        <div className={styles.popoverContentContainer}>
                            {event.beaconSnapshot.map((beacon, index) => (
                                <TaskInfoItem
                                    key={index}
                                    title={t(
                                        'shared.tasks.beacon_name_with_distance',
                                        {
                                            name: beacon.name,
                                            distance: beacon.distanceMeters
                                        }
                                    )}
                                    tags={beacon.tags.map((tag) => ({
                                        text: tag
                                    }))}
                                    listNumber={index + 1}
                                />
                            ))}
                        </div>
                    </UIPopover>
                </div>
            )}
            {!seeMore && hasItems && (
                <div
                    className={`${styles.eventItemDataItem} ${styles.eventItemDataItemSpacer}`}
                >
                    <UITextButton
                        variant="primary"
                        onClick={() => setSeeMore(true)}
                    >
                        {t('components.task_events_list.see_more')}
                    </UITextButton>
                </div>
            )}
            {seeMore && hasItems && (
                <div
                    className={styles.eventItemDataItemSpacer}
                    style={{
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                        gap: 12,
                        padding: 12,
                        borderRadius: 8,
                        backgroundColor: moonlight
                    }}
                >
                    {items()}

                    <div>
                        <UITextButton
                            variant="primary"
                            onClick={() => setSeeMore(false)}
                        >
                            {t('components.task_events_list.see_less')}
                        </UITextButton>
                    </div>
                </div>
            )}
        </div>
    );
}

type TaskEventActionProps = {
    task: Task;
};
function TaskEventAction(props: TaskEventActionProps): ReactElement {
    const { task } = props;

    const { me } = useMe();

    const { canAddNote, canAssign, canResolve } = useTaskPermissions(task);

    const shouldRenderAddNoteButton = canAddNote;

    const shouldRenderAcknowledgeButton =
        canAssign &&
        task.simpleStatus === 'ALERTED' &&
        !!task.alert &&
        !task.alert.acknowledged;

    const shouldRenderAssignButton =
        canAssign &&
        task.simpleStatus === 'ALERTED' &&
        !!task.alert &&
        task.alert.acknowledged &&
        (!task.alert.assigned || task.alert.assigned.user.id !== me.id);

    const shouldRenderResolveButton =
        canResolve &&
        task.simpleStatus === 'ALERTED' &&
        !!task.alert &&
        task.alert.assigned &&
        task.alert.assigned.user.id === me.id;

    const shouldRender =
        shouldRenderAddNoteButton ||
        shouldRenderAcknowledgeButton ||
        shouldRenderAssignButton ||
        shouldRenderResolveButton;

    if (!shouldRender) {
        return <></>;
    }

    return (
        <div
            style={{
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                alignItems: 'center',
                padding: 16,
                gap: 8,
                backgroundColor: moonlight + opacity(0.5)
            }}
        >
            {shouldRenderAcknowledgeButton ? (
                <AcknowledgeAlertButton taskId={task.id} />
            ) : undefined}

            {shouldRenderAssignButton ? (
                <AssignAlertButton taskId={task.id} />
            ) : undefined}

            {shouldRenderResolveButton ? (
                <ResolveAlertButton taskId={task.id} />
            ) : undefined}

            {shouldRenderAddNoteButton ? (
                <AddNoteButton taskId={task.id} />
            ) : undefined}
        </div>
    );
}
